import { useTranslation } from 'react-i18next';

interface WeightPoint {
  measured_at: string;
  weight_grams: number;
}

/**
 * Dependency-free inline-SVG weight trend (one series). Renders nothing for &lt;2 points;
 * the caller shows the vitals table regardless. Weight axis labelled in kg.
 */
export function WeightChart({ points }: { points: WeightPoint[] }): JSX.Element | null {
  const { t } = useTranslation();
  const data = points.filter((p) => p.weight_grams != null);
  if (data.length < 2) return null;

  const W = 360;
  const H = 110;
  const pad = 22;
  const weights = data.map((d) => d.weight_grams);
  const min = Math.min(...weights);
  const max = Math.max(...weights);
  const range = max - min || 1;
  const n = data.length;
  const x = (i: number): number => pad + (i / (n - 1)) * (W - 2 * pad);
  const y = (w: number): number => H - pad - ((w - min) / range) * (H - 2 * pad);
  const polyline = data.map((d, i) => `${x(i).toFixed(1)},${y(d.weight_grams).toFixed(1)}`).join(' ');
  const kg = (g: number): string => (g / 1000).toFixed(2);
  const last = data[data.length - 1];
  if (!last) return null;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="text-primary w-full"
      role="img"
      aria-label={t('clinical.weight_chart_aria', { kg: kg(last.weight_grams) })}
    >
      {/* axis labels (kg) */}
      <text x={2} y={y(max) + 4} className="fill-muted-foreground" fontSize="9">{kg(max)}</text>
      <text x={2} y={y(min) + 4} className="fill-muted-foreground" fontSize="9">{kg(min)}</text>
      <polyline points={polyline} fill="none" stroke="currentColor" strokeWidth="1.5" />
      {data.map((d, i) => (
        <circle key={d.measured_at + i} cx={x(i)} cy={y(d.weight_grams)} r="2.5" fill="currentColor" />
      ))}
      <text x={W - pad} y={y(last.weight_grams) - 6} textAnchor="end" className="fill-foreground" fontSize="10">
        {kg(last.weight_grams)} kg
      </text>
    </svg>
  );
}
